import type { Scorecard } from './SessionTracker';

export interface ShareCardData {
  openInvoices: number;
  craftScore: number;
  dso: number | null;
  openDisputes: number;
  count90Plus: number;
}

/** Render the share text block per spec §9. URL is a placeholder until
 *  the deployment URL is finalized. */
export const renderShareText = (data: ShareCardData): string => {
  const lines: string[] = [
    'AR RUSH — MONTH-END CLOSE',
    '',
    `I finished the day with ${data.openInvoices} open invoices on my desk.`,
    'This is what real AR clerks do every day.',
    'Without software.',
    '',
    `Craft score: ${data.craftScore}`,
    'Words learned: aging · dunning · cash app · short-pay · dispute · promise-to-pay · write-off · DSO',
    `DSO today: ${data.dso ?? '—'} days`,
    `Tomorrow starts with: ${data.openInvoices} open, ${data.openDisputes} disputes, ${data.count90Plus} in 90+`,
    '',
    'fazeshift.com/ar-rush',
  ];
  return lines.join('\n');
};

/** Aggregate session data + email-state into share-card values. */
export const buildShareData = (
  scorecard: Readonly<Scorecard>,
  unreadEmailCount: number,
  count90Plus: number,
): ShareCardData => {
  const craftScore =
    scorecard.dunningRightBucket * 10 +
    scorecard.cashCorrectCustomer * 15 +
    scorecard.shortPaysResolved * 10 +
    scorecard.disputesLogged * 8 +
    scorecard.promiseToPaySolid * 12 +
    scorecard.promiseToPaySoft * 4 +
    scorecard.writeOffsApproved * 5;

  return {
    openInvoices: unreadEmailCount,
    craftScore,
    dso: scorecard.dsoAtClose,
    openDisputes: scorecard.disputesLogged,
    count90Plus,
  };
};

/** Copy text to clipboard. Returns true on success. */
export const copyShareText = async (text: string): Promise<boolean> => {
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to no-op on permission denial / non-secure context
  }
  return false;
};

/** Render the share card to a PNG via an offscreen canvas + trigger
 *  a browser download. Best-effort. */
export const downloadShareCardImage = (data: ShareCardData): void => {
  if (typeof document === 'undefined') return;
  const canvas = document.createElement('canvas');
  canvas.width = 700;
  canvas.height = 400;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  ctx.fillStyle = '#0f1420';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 22px system-ui, sans-serif';
  ctx.fillText('AR RUSH — MONTH-END CLOSE', 32, 50);

  ctx.font = '15px system-ui, sans-serif';
  ctx.fillStyle = '#e6e9f0';
  const wrapped = [
    `I finished the day with ${data.openInvoices} open invoices on my desk.`,
    'This is what real AR clerks do every day. Without software.',
  ];
  wrapped.forEach((line, i) => ctx.fillText(line, 32, 90 + i * 22));

  ctx.fillStyle = '#3a4050';
  ctx.fillRect(32, 150, canvas.width - 64, 1);

  ctx.fillStyle = '#ffffff';
  ctx.font = '13px system-ui, sans-serif';
  ctx.fillText(`Craft score: ${data.craftScore}`, 32, 180);
  ctx.fillText('Words learned: aging · dunning · cash app · short-pay ·', 32, 205);
  ctx.fillText('         dispute · promise-to-pay · write-off · DSO', 32, 224);
  ctx.fillText(`DSO today: ${data.dso ?? '—'} days`, 32, 250);
  ctx.fillText(
    `Tomorrow starts with: ${data.openInvoices} open, ${data.openDisputes} disputes, ${data.count90Plus} in 90+`,
    32,
    275,
  );

  ctx.fillStyle = '#8a95a8';
  ctx.font = '12px system-ui, sans-serif';
  ctx.fillText('fazeshift.com/ar-rush', 32, canvas.height - 32);

  const link = document.createElement('a');
  link.download = 'ar-rush-share-card.png';
  link.href = canvas.toDataURL('image/png');
  link.click();
};
